/* eslint-disable */
import { useState, useCallback, useEffect, useRef, FC } from 'react';
import * as MapGL from 'react-map-gl';
import { zonesLayers } from './layerStyles';
import { useMemo } from 'react';
import mapboxgl from 'mapbox-gl'; // This is a dependency of react-map-gl even if you didn't explicitly install it
import { Area, Zone, Country } from '@/models/location';
import { Placeholder } from './Placeholder';
import { MAPBOX_TOKEN } from '@/features/map/constants';
import { useGeoJson } from '@/features/map';

type Props = {
  country?: Country;
  area?: Area;
  zones?: Zone[];
  selectedZones: number[];
  height?: string;
  width?: string;
  onZoneSelect?: (e: mapboxgl.MapLayerMouseEvent) => void;
};

export const MapWithSelection: FC<Props> = ({
  zones,
  area,
  country,
  height = '400px',
  width = '100%',
  onZoneSelect,
  selectedZones,
}) => {
  const { geojson } = useGeoJson({ zones });
  const [hovered, setHovered] = useState<string>('');
  const [loaded, setLoaded] = useState(false);

  const filterSelected = useMemo(() => {
    return [
      'all',
      ['in', 'id', ...selectedZones], // Ensure selected is an array of valid values
    ];
  }, [selectedZones]);

  const filterHovered = useMemo(() => {
    return [
      'all',
      ['in', 'id', hovered], // Ensure hovered is a valid value
    ];
  }, [hovered]);

  const mapRef = useRef<MapGL.MapRef>(null);

  const handleHover = useCallback((e: any) => {
    const id = e.features?.length && e.features[0].properties.id;
    setHovered(id || '');
  }, []);

  const setLocation = useCallback(
    ({
      longitude,
      latitude,
      zoom,
    }: {
      longitude: number;
      latitude: number;
      zoom: number;
    }) => {
      mapRef.current?.flyTo({
        center: [longitude, latitude],
        duration: 2000,
        zoom,
      });
    },
    []
  );

  useEffect(() => {
    if (area && loaded) {
      const { longitude, latitude } = area;
      setLocation({ longitude, latitude, zoom: 9 });
    }
  }, [area, loaded]);

  useEffect(() => {
    if (country && loaded) {
      const { longitude, latitude } = country;
      setLocation({ longitude, latitude, zoom: 5 });
    }
  }, [country, loaded]);

  return (
    <div className="relative rounded-md overflow-hidden">
      <Placeholder hidden={loaded} />
      <MapGL.Map
        ref={mapRef}
        style={{ width, height }}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle="mapbox://styles/mapbox/light-v9"
        onClick={onZoneSelect}
        onMouseMove={handleHover}
        onLoad={() => setLoaded(true)}
        scrollZoom={false}
        interactiveLayerIds={[
          'zones-fill',
          'zones-fill-selected',
          'zones-fill-hovered',
        ]}
        initialViewState={{
          longitude: 11.582423085886072,
          latitude: 50.0535131370207,
          zoom: 3,
        }}
      >
        <MapGL.NavigationControl
          style={{
            right: 10,
            top: 10,
          }}
        />
        {geojson && geojson.features.length > 0 && (
          <MapGL.Source id="zones" type="geojson" data={geojson}>
            <MapGL.Layer
              id="zones-fill"
              type="fill"
              source="zones"
              paint={zonesLayers.fill.default}
            />
            <MapGL.Layer
              id="zones-fill-selected"
              type="fill"
              source="zones"
              paint={zonesLayers.fill.selected}
              filter={filterSelected}
            />
            <MapGL.Layer
              id="zones-fill-hovered"
              type="fill"
              source="zones"
              paint={zonesLayers.fill.hovered}
              filter={filterHovered}
            />
            <MapGL.Layer
              id="zones-outline"
              type="line"
              source="zones"
              paint={zonesLayers.outline.default}
            />
            <MapGL.Layer
              id="zones-outline-selected"
              type="line"
              source="zones"
              paint={zonesLayers.outline.selected}
              filter={filterSelected}
            />
            <MapGL.Layer
              id="zones-outline-hovered"
              type="line"
              source="zones"
              paint={zonesLayers.outline.hovered}
              filter={filterHovered}
            />
          </MapGL.Source>
        )}
      </MapGL.Map>
    </div>
  );
};
